import type {ReactNode, HTMLAttributes, Ref, CSSProperties} from 'react'
import {cn} from '@/lib/utils'

type DraggableRowVariant = 'palette' | 'chain'

interface DraggableRowProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode
    /** `palette` = compact tiny row, `chain` = larger bordered card row. */
    variant: DraggableRowVariant
    /** Chain variant: the item is locked/read-only (muted background). */
    locked?: boolean
    /** Chain variant: the item is the currently selected one (primary ring). */
    selected?: boolean
    /** Chain variant: the item has a validation error (destructive border). */
    hasError?: boolean
    /** Chain variant: the item is actively being dragged (elevated shadow). */
    dragging?: boolean
    innerRef?: Ref<HTMLDivElement>
    style?: CSSProperties
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const variantClasses: Record<DraggableRowVariant, string> = {
    palette: 'flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-grab hover:bg-muted/50',
    chain: 'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors cursor-pointer',
}
const chainStateClasses = {
    default: 'bg-background',
    locked: 'bg-muted text-muted-foreground',
    selected: 'ring-2 ring-primary border-primary',
    error: 'border-destructive',
    dragging: 'shadow-lg',
}
/* eslint-enable i18next/no-literal-string */

/**
 * Horizontal row used in drag-and-drop task builders. `variant="palette"`
 * renders the compact draggable items in a task palette; `variant="chain"`
 * renders the larger cards inside a task chain with selection/error/drag state.
 */
export function DraggableRow({
    children,
    variant,
    locked,
    selected,
    hasError,
    dragging,
    innerRef,
    className,
    style,
    ...rest
}: DraggableRowProps) {
    return (
        <div
            ref={innerRef}
            style={style}
            className={cn(
                variantClasses[variant],
                variant === 'chain' && (locked ? chainStateClasses.locked : chainStateClasses.default),
                variant === 'chain' && selected && chainStateClasses.selected,
                variant === 'chain' && hasError && chainStateClasses.error,
                variant === 'chain' && dragging && chainStateClasses.dragging,
                className,
            )}
            {...rest}
        >
            {children}
        </div>
    )
}
